import { useEffect, useRef } from 'react'

import { Avatar, Box, Dialog, ThemeProvider, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useLocation } from 'react-router-dom'

import { StepUpAuthDialog } from '@/components/auth/step-up-auth-dialog'
import { Button } from '@/components/ui/button'
import { AlertTriangleIcon, CheckCircleIcon, InfoCircleIcon } from '@/components/ui/icons'
import { LoadingScreen } from '@/components/ui/loading-screen'
import { theme } from '@/lib/theme'
import { useAppState } from '@/states/app-state'

import { DialogTransition } from './dialog-transition'
import { SnackbarCloseAction } from './snackbar-close-action'

interface Props {
  children: React.ReactNode
}

interface LastToast {
  message: string | React.ReactElement
  timestamp: number
}

export function AppProvider({ children }: Props) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const { isLoading, messageDialogOptions, hideMessageDialog, messageToastOptions } = useAppState()

  // Payroll detail has its own loader; skip the global axios backdrop there to avoid a double overlay
  const { pathname } = useLocation()
  const isPayrollDetail = /^\/payrolls\/\d+(\/|$)/.test(pathname)

  const lastToastRef = useRef<LastToast | null>(null)

  useEffect(() => {
    if (!messageToastOptions) return

    const now = Date.now()
    const last = lastToastRef.current

    // Skip duplicate toasts with the same message within 3 seconds
    if (last?.message === messageToastOptions.message && now - last.timestamp < 3000) return

    lastToastRef.current = { message: messageToastOptions.message, timestamp: now }

    enqueueSnackbar({
      message: <Box sx={{ whiteSpace: 'pre-line' }}>{messageToastOptions.message}</Box>,
      variant: messageToastOptions.variant,
      autoHideDuration: 4000,
      action: (id) => (
        <>
          {messageToastOptions.action?.(() => closeSnackbar(id))}
          <SnackbarCloseAction snackbarId={id} />
        </>
      ),
    })
  }, [enqueueSnackbar, closeSnackbar, messageToastOptions])

  const isMessageDialogOpen = !!messageDialogOptions

  return (
    <>
      {children}
      <ThemeProvider theme={theme}>
        <LoadingScreen show={isLoading && !isPayrollDetail} />
        <Dialog
          open={isMessageDialogOpen}
          keepMounted
          TransitionComponent={DialogTransition}
          onClose={() => hideMessageDialog()}
          sx={{
            '& .MuiDialog-paper': {
              borderWidth: '2px 0 0 0',
              borderTopColor: `${messageDialogOptions?.variant}.light`,
            },
          }}
        >
          {messageDialogOptions && (
            <Box display="flex" flexDirection="column" gap={2} p={2}>
              <Avatar sx={{ bgcolor: `${messageDialogOptions.variant}.main`, width: 44, height: 44 }}>
                {messageDialogOptions.variant === 'warning' && <AlertTriangleIcon sx={{ color: 'common.white' }} />}
                {messageDialogOptions.variant === 'success' && <CheckCircleIcon sx={{ color: 'common.white' }} />}
                {(messageDialogOptions.variant === 'info' || messageDialogOptions.variant === 'error') && (
                  <InfoCircleIcon sx={{ color: 'common.white' }} />
                )}
              </Avatar>
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="h6">{messageDialogOptions.title}</Typography>
                <Typography variant="subtitle2" sx={{ whiteSpace: 'pre-line' }}>
                  {messageDialogOptions.message}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="center">
                <Button variant="outlined" color="inherit" size="small" onClick={() => hideMessageDialog()}>
                  Close
                </Button>
              </Box>
            </Box>
          )}
        </Dialog>
        <StepUpAuthDialog />
      </ThemeProvider>
    </>
  )
}
